"use client";

import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useEffect } from "react";
import ButtonIcon from "./buttons/icon";
import View from "../atoms/view";
import cn from "@/utils/cn";

/**
 * @name Modal
 * @description Renders a modal dialog that overlays the page content.
 *
 * @param {ModalProps} props - Component props.
 * @param {Dispatch<SetStateAction<boolean>>} props.toggleOpen - Function to toggle the modal open state.
 * @param {boolean} props.open - Whether the modal is currently open.
 * @param {ReactNode} props.children - The content to display inside the modal.
 * @param {string} [props.height] - Custom height class for the modal content area.
 * @param {string} [props.width] - Custom width class for the modal dialog.
 * @returns {JSX.Element} The rendered modal component.
 */

export interface ModalProps {
  toggleOpen: Dispatch<SetStateAction<boolean>>;
  open: boolean;
  children: ReactNode;
  height?: string;
  width?: string;
}
const Modal = ({ children, toggleOpen, open, height, width }: ModalProps) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "visible";
    }

    return () => {
      document.body.style.overflow = "visible";
    };
  }, [open]);

  return (
    <View
      className={cn(
        "inset-0 fixed z-[105] bg-black/40 backdrop-blur-sm opacity-0 invisible duration-500 flex items-center justify-center",
        { "visible opacity-100": open }
      )}
    >
      <View
        className={`flex items-center justify-center w-full px-4 ${height || "min-h-screen"}`}
      >
        <View
          className={cn(
            "relative duration-300 max-w-[710px] modal scale-75 w-full box xl:p-8 translate-y-20 opacity-0",
            { "translate-y-0 scale-100 opacity-100": open },
            width
          )}
        >
          <ButtonIcon
            action={() => toggleOpen(!open)}
            className="absolute top-0 ltr:right-0 rtl:left-0 p-2"
            iconLeft={{ name: "fa-regular fa-circle-xmark fa-xl" }}
            aria-label="Close modal"
          />
          <View className="relative p-3 md:p-0">{children}</View>
        </View>
      </View>
    </View>
  );
};

export default Modal;
